import { useCurrentUserStore } from '@infuse/iam/user/store';
import { i18n } from '@infuse/utils';
import type { FunctionDirective } from 'vue';

/**
 * v-permission Directive
 *
 * Controls element visibility or interactivity based on user permissions.
 *
 * Usage:
 *   <button v-permission="{ id: 'permission.id', check: 'any', hide: true }">Action</button>
 *
 * Or multiple ids:
 *   <button v-permission="{ ids: ['permission.id1', 'permission.id2'], check: 'any', hide: true }">Action</button>
 *
 * Options:
 *   - id:    Single permission ID to check (required)
 *   - ids:   Array of permission IDs to check (required)
 *   - check: 'any' (default, user needs any listed permission) or 'all' (user needs all)
 *   - hide:  true (default, hides element if not allowed), false (disables/dims instead)
 *   - grantFalsyIds: Whether to treat falsy permission IDs as allowed (defaults to false)
 *
 */

interface PermissionDirectiveValue {
  id?: string;
  ids?: string[];
  check?: 'any' | 'all';
  useSubscriptionId?: boolean;
  hide?: boolean; // Defaults to true if not provided
  grantFalsyIds?: boolean;
}

function onPermissionCheck(): FunctionDirective<HTMLElement, PermissionDirectiveValue> {
  return (el, binding) => {
    const { permissions, enabled, mainSubscriptionId } = useCurrentUserStore();

    if (!enabled) {
      return;
    }

    const { hide = true, check = 'any', useSubscriptionId = false, grantFalsyIds = false } = binding.value;
    let { ids = [] } = binding.value;
    const { id } = binding.value;

    if (grantFalsyIds) {
      if (check === 'any' && (ids.some((id) => !id) || (!ids.length && !id))) {
        return;
      } else if (check === 'all' && ids.length > 0 && ids.every((id) => !id)) {
        return;
      } else if (check === 'all' && !ids.length && !id) {
        return;
      }
    }

    ids = ids.filter((id) => !!id);

    if (!ids.length && !id) {
      // No ids to check permissions on
      return;
    }

    if (!ids.length && id) {
      ids = [id];
    }

    // Check if any of the ids are included in permissions
    const checkCondition = useSubscriptionId
      ? (id: string) => permissions.some((p) => p.permissionId === id && p.subscriptionId === mainSubscriptionId)
      : (id: string) => permissions.some((p) => p.permissionId === id);

    if (check === 'any') {
      if (ids.some(checkCondition)) {
        return;
      }
    } else {
      if (ids.every(checkCondition)) {
        return;
      }
    }

    if (hide) {
      el.style.display = 'none';
    } else {
      el.style.opacity = '0.25';
      el.style.cursor = 'not-allowed';
      el.title = i18n.global.t('iam.permissions.directive.noPermission');

      // Prevent click events
      const preventClick = (e: Event) => {
        e.stopPropagation();
        e.preventDefault();
      };
      el.addEventListener('click', preventClick, true);
    }
  };
}

export const vPermission = onPermissionCheck();
